import { sendEmailVerification } from 'firebase/auth';

import {
  type ISignUpPayload,
  authApiService,
  getAuthApiErrorCode,
  getAuthApiStatus,
} from '../features/auth/api/auth-api';
import {
  API_CACHE_KEYS,
  ApiResponseCache,
  clearAllStartedTrainingCaches,
} from '../redux/api-response-cache';
import { clearAuthTokenCache } from '../shared/auth/auth-token-holder';
import { Language } from '../shared/constants';
import { auth } from '../shared/firebase/firebase-client';
import {
  LocalStorageKey,
  LocalStorageService,
} from '../shared/storage/local-storage';
import { logAuth } from '../utils/auth/auth-debug-logger';
import { clearUserScopedApiCaches } from '../utils/auth/backend-session-cache';
import { useFirebaseAuthInterface } from './_use-firebase-auth-interface';

interface IAuthData {
  email: string;
  password: string;
  firstname?: string;
  lastname?: string;
  login?: string;
}

export function useFirebaseAuth() {
  const {
    getIdToken,
    createUserWithEmailAndPassword,
    signInWithEmailAndPassword,
    sendPasswordResetEmail,
    verifyPasswordResetCode,
    confirmPasswordReset: confirmPasswordResetFB,
    checkActionCode: checkActionCodeFb,
    applyActionCode: applyActionCodeFb,
    signInWithGoogle: signInWithGoogleFb,
    signInWithApple: signInWithAppleFb,
    signInWithFacebook: signInWithFacebookFb,
    signOut,
    updatePassword,
    getCurrentUser,
    linkWithEmailAndPassword: linkWithEmailAndPasswordFb,
    linkWithPopup: linkWithPopupFb,
  } = useFirebaseAuthInterface();

  const getToken = async (): Promise<string | undefined> => {
    return getIdToken();
  };
  const getSignUpLanguageKey = (): Language => {
    const languageKey = LocalStorageService.getValue(LocalStorageKey.language);
    return Object.values(Language).includes(languageKey as Language)
      ? (languageKey as Language)
      : Language.en;
  };

  type FirebaseRestError = {
    code?: string | number;
    message?: string;
    error?: { code?: number; message?: string };
    errors?: Array<{ message?: string }>;
  };

  function getFirebaseRestMessage(err: unknown): string | undefined {
    const e = err as FirebaseRestError;
    return e?.error?.message ?? e?.message ?? e?.errors?.[0]?.message;
  }

  function isFirebaseEmailExistsError(err: unknown): boolean {
    const e = err as FirebaseRestError;
    if (e?.code === 'auth/email-already-in-use') return true;
    if (e?.code === 400 && e?.message === 'EMAIL_EXISTS') return true;
    if (getFirebaseRestMessage(err) === 'EMAIL_EXISTS') return true;
    return false;
  }

  function isFirebaseInvalidCredentialsError(err: unknown): boolean {
    const e = err as FirebaseRestError;
    if (e?.code === 'auth/invalid-credential') return true;
    if (e?.code === 400 && e?.message === 'INVALID_LOGIN_CREDENTIALS')
      return true;
    if (getFirebaseRestMessage(err) === 'INVALID_LOGIN_CREDENTIALS')
      return true;
    return false;
  }

  const signUpWithPassword = async (data: IAuthData): Promise<void> => {
    let response: {
      user: { uid: string; email?: string; displayName?: string };
    } | null = null;

    try {
      response = await createUserWithEmailAndPassword({
        email: data.email,
        password: data.password,
      });
    } catch (err: unknown) {
      if (!isFirebaseEmailExistsError(err)) {
        throw err;
      }

      const existsOnBackend = await authApiService.checkEmailExists(data.email);

      if (existsOnBackend) {
        try {
          response = await signInWithEmailAndPassword({
            email: data.email,
            password: data.password,
          });
        } catch (signInErr: unknown) {
          if (isFirebaseInvalidCredentialsError(signInErr)) {
            throw { code: 'auth/email-exists-use-sign-in' };
          }
          throw signInErr;
        }
      } else {
        response = await createUserWithEmailAndPassword({
          email: data.email,
          password: data.password,
        });
      }
    }

    if (!response || !response.user) {
      throw new Error('Failed to sign up');
    }

    const accessToken = await getIdToken();
    if (!accessToken) throw new Error('Failed to get token');

    try {
      const payload: ISignUpPayload = {
        login: data.login ?? data.email ?? '',
        email: response.user.email ?? data.email,
        uid: response.user.uid,
        languageKey: getSignUpLanguageKey(),
      };
      if (data.firstname?.trim()) {
        payload.firstname = data.firstname.trim();
      }
      if (data.lastname?.trim()) {
        payload.lastname = data.lastname.trim();
      }
      await authApiService.signUp(payload, accessToken);
    } catch (backendErr: unknown) {
      const status = getAuthApiStatus(backendErr);
      const code = getAuthApiErrorCode(backendErr);
      if (status === 403 && code === 'USER_ALREADY_EXISTS') {
        throw { code: 'signUp/backendUserExists' };
      }
      throw backendErr;
    }

    await sendEmailVerification(auth.currentUser!);
  };

  const logInWithPassword = async (data: {
    email: string;
    password: string;
  }): Promise<void> => {
    const response = await signInWithEmailAndPassword({
      email: data.email.trim(),
      password: data.password,
    });

    if (!response || !response.user) {
      throw new Error('Failed to log in');
    }
    // AuthContext will call signInApi with email + uid on auth state change.
  };

  const resetPassword = async (email: string): Promise<void> => {
    return sendPasswordResetEmail(email);
  };

  const confirmPasswordReset = async (
    oobCode: string,
    newPassword: string,
  ): Promise<void> => {
    await verifyPasswordResetCode(oobCode);
    await confirmPasswordResetFB({ oobCode, newPassword });
  };

  const checkActionCode = async (oobCode: string): Promise<string> => {
    const { data } = await checkActionCodeFb(oobCode);
    return data?.email;
  };

  const applyActionCode = async (oobCode: string): Promise<void> => {
    await applyActionCodeFb(oobCode);
  };

  const signInWithGoogle = async (): Promise<void> => {
    const response = await signInWithGoogleFb();

    if (!response || !response.user) {
      throw new Error('Failed to log in');
    }
  };

  const signInWithApple = async (): Promise<void> => {
    const response = await signInWithAppleFb();

    if (!response || !response.user) {
      throw new Error('Failed to log in');
    }
  };

  const signInWithFacebook = async (): Promise<void> => {
    const response = await signInWithFacebookFb();

    if (!response || !response.user) {
      throw new Error('Failed to log in');
    }
  };

  const signUpWithGoogle = async (): Promise<void> => {
    const response = await signInWithGoogleFb();

    if (!response || !response.user) {
      throw new Error('Failed to sign up');
    }

    const accessToken = await getIdToken();
    if (!accessToken) throw new Error('Failed to get token');

    try {
      await authApiService.signUp(
        {
          login: response.user.email ?? response.user.uid,
          email: response.user.email ?? undefined,
          uid: response.user.uid,
          languageKey: getSignUpLanguageKey(),
        },
        accessToken,
      );
    } catch (backendErr: unknown) {
      const status = getAuthApiStatus(backendErr);
      const code = getAuthApiErrorCode(backendErr);
      if (status === 403 && code === 'USER_ALREADY_EXISTS') {
        throw { code: 'signUp/backendUserExists' };
      }
      throw backendErr;
    }
  };

  const signUpWithApple = async (): Promise<void> => {
    const response = await signInWithAppleFb();

    if (!response || !response.user) {
      throw new Error('Failed to sign up');
    }

    const accessToken = await getIdToken();
    if (!accessToken) throw new Error('Failed to get token');

    try {
      await authApiService.signUp(
        {
          login: response.user.email ?? response.user.uid,
          email: response.user.email ?? undefined,
          uid: response.user.uid,
          languageKey: getSignUpLanguageKey(),
        },
        accessToken,
      );
    } catch (backendErr: unknown) {
      const status = getAuthApiStatus(backendErr);
      const code = getAuthApiErrorCode(backendErr);
      if (status === 403 && code === 'USER_ALREADY_EXISTS') {
        throw { code: 'signUp/backendUserExists' };
      }
      throw backendErr;
    }
  };

  const signUpWithFacebook = async (): Promise<void> => {
    const response = await signInWithFacebookFb();

    if (!response || !response.user) {
      throw new Error('Failed to sign up');
    }

    const accessToken = await getIdToken();
    if (!accessToken) throw new Error('Failed to get token');

    try {
      await authApiService.signUp(
        {
          login: response.user.email ?? response.user.uid,
          email: response.user.email ?? undefined,
          uid: response.user.uid,
          languageKey: getSignUpLanguageKey(),
        },
        accessToken,
      );
    } catch (backendErr: unknown) {
      const status = getAuthApiStatus(backendErr);
      const code = getAuthApiErrorCode(backendErr);
      if (status === 403 && code === 'USER_ALREADY_EXISTS') {
        throw { code: 'signUp/backendUserExists' };
      }
      throw backendErr;
    }
  };

  const logOut = (): Promise<void> => {
    logAuth('firebaseAuth:logOut:start');
    clearUserScopedApiCaches();
    clearAuthTokenCache();
    clearAllStartedTrainingCaches();
    return signOut().then(() => {
      logAuth('firebaseAuth:logOut:done');
    });
  };

  const verifyApi = async (email: string): Promise<unknown> => {
    return authApiService.verify(email);
  };

  const changePassword = async (
    email: string,
    currentPassword: string,
    newPassword: string,
  ): Promise<void> => {
    await signInWithEmailAndPassword({ email, password: currentPassword });
    return updatePassword(newPassword);
  };

  const sendVerificationEmail = async () => {
    const user = auth.currentUser;
    if (!user) return;
    await sendEmailVerification(user);
  };

  const linkWithEmailAndPassword = async (email: string, password: string) => {
    return linkWithEmailAndPasswordFb({ email, password });
  };

  const linkWithPopup = async (provider: 'google' | 'apple' | 'facebook') => {
    return linkWithPopupFb(provider);
  };

  return {
    verifyApi,
    checkActionCode,
    applyActionCode,
    getToken,
    getCurrentUser,
    signUpWithPassword,
    signUpWithGoogle,
    signUpWithApple,
    signUpWithFacebook,
    logInWithPassword,
    resetPassword,
    confirmPasswordReset,
    signInWithGoogle,
    signInWithApple,
    signInWithFacebook,
    logOut,
    changePassword,
    sendVerificationEmail,
    linkWithEmailAndPassword,
    linkWithPopup,
  };
}
